import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getGuide,
  listConductores,
  listVehiculos,
} from '../services/guidesService';
import { formatDate } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function GuideDetailPage() {
  const { id } = useParams();
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [guia, setGuia] = useState(null);
  const [conductores, setConductores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const g = await getGuide(id);
      setGuia(g);
      const [c, v] = await Promise.all([
        listConductores({ ruc, pageSize: 5 }).catch(() => []),
        listVehiculos({ ruc, pageSize: 5 }).catch(() => []),
      ]);
      setConductores(c);
      setVehiculos(v);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id, ruc]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando guía…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!guia) return <EmptyState title="Guía no encontrada" />;

  const dest = guia.destinatario || {};
  const items = Array.isArray(guia.items) ? guia.items : [];
  const transportista = guia.transportista || {};

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/guias">Guías</Link> / {guia.serie}-{guia.correlativo}
          </p>
          <h1>
            {guia.serie || '—'}-{guia.correlativo ?? '—'}
          </h1>
          <p className="muted">
            {dest.rznSocial || guia.rznSocial || 'Destinatario'} · {formatDate(guia.fechaEmision || guia.createdAt)}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
          {guia.pdf ? (
            <a className="btn btn-primary" href={guia.pdf} target="_blank" rel="noreferrer">
              Descargar PDF
            </a>
          ) : null}
          {guia.xml ? (
            <a className="btn btn-secondary" href={guia.xml} target="_blank" rel="noreferrer">
              Descargar XML
            </a>
          ) : null}
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">SUNAT</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {guia.aceptado ? <Badge tone="ok">Aceptado</Badge> : <Badge tone="warn">No aceptado</Badge>}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {guia.status || '—'}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Ítems</p>
          <p className="stat-value">{items.length || '—'}</p>
        </div>
      </section>

      <section className="meta-grid">
        <div>
          <span className="muted">Destinatario doc</span>
          <div>{dest.numDoc || '—'}</div>
        </div>
        <div>
          <span className="muted">Destinatario</span>
          <div>{dest.rznSocial || '—'}</div>
        </div>
        <div>
          <span className="muted">Dirección partida</span>
          <div>{guia.partida?.direccion || guia.direccionPartida || '—'}</div>
        </div>
        <div>
          <span className="muted">Dirección llegada</span>
          <div>{guia.llegada?.direccion || guia.direccionLlegada || '—'}</div>
        </div>
        <div>
          <span className="muted">Transportista</span>
          <div>{transportista.rznSocial || transportista.nombre || '—'}</div>
        </div>
        <div>
          <span className="muted">Vehículo</span>
          <div>{transportista.placa || guia.placa || '—'}</div>
        </div>
        {guia.cdr ? (
          <div>
            <span className="muted">CDR</span>
            <div>
              <a href={guia.cdr} target="_blank" rel="noreferrer">
                Descargar CDR
              </a>
            </div>
          </div>
        ) : null}
      </section>

      {items.length ? (
        <section className="panel">
          <header className="panel-header">
            <h2>Productos</h2>
          </header>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cant.</th>
                  <th>Unidad</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.name || it.descripcion || '—'}</td>
                    <td>{it.quantity ?? it.cantidad ?? '—'}</td>
                    <td>{it.unidad || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="panel-grid">
        <article className="panel">
          <header className="panel-header">
            <h2>Conductores de la empresa</h2>
          </header>
          {conductores.length === 0 ? (
            <EmptyState title="Sin conductores" />
          ) : (
            <ul className="simple-list">
              {conductores.map((c) => (
                <li key={c.id}>
                  <div>
                    <strong>{c.nombre}</strong>
                    <div className="muted">
                      {c.tipoDocumento} {c.nroDocumento}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
        <article className="panel">
          <header className="panel-header">
            <h2>Vehículos de la empresa</h2>
          </header>
          {vehiculos.length === 0 ? (
            <EmptyState title="Sin vehículos" />
          ) : (
            <ul className="simple-list">
              {vehiculos.map((v) => (
                <li key={v.id}>
                  <div>
                    <strong>{v.placa}</strong>
                    <div className="muted">
                      {v.marca} {v.descripcion}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>

      <p className="muted">
        No se reimplementa MotorNumeracion ni la integración SUNAT aquí. Las guías se
        generan y consultan con la infraestructura existente.
      </p>
    </div>
  );
}
